import type {
	ChangelogAction,
	ChangelogEntry,
	ChangelogResourceType,
} from '@orderlymeds/measurements-schemas';
import { useChangelog } from '../api/changelog';
import { formatRelativeTime } from '../utils/format';
import styles from './ChangelogTimeline.module.css';

// --- Props ---

type ChangelogTimelineProps =
	| {
			projectId: string;
			resourceType: ChangelogResourceType | string;
			resourceId: string;
	  }
	| {
			projectId: string;
			scope: { include: ChangelogResourceType[] };
	  };

// --- Sub-components ---

function ActionBadge({ action }: { action: ChangelogAction }) {
	const labels: Record<ChangelogAction, string> = {
		create: 'Created',
		update: 'Updated',
		status_change: 'Status',
		delete: 'Deleted',
	};
	return (
		<span className={[styles.badge, styles[`badge_${action}`]].join(' ')}>{labels[action]}</span>
	);
}

function ChangelogEntryRow({ entry }: { entry: ChangelogEntry }) {
	const hasDiff = entry.previous_state !== null || entry.new_state !== null;

	return (
		<li className={styles.entry}>
			<div className={styles.entryMeta}>
				<span className={styles.timestamp}>{formatRelativeTime(entry.timestamp)}</span>
				<span className={styles.user}>{entry.user_email}</span>
				<ActionBadge action={entry.action} />
				<span className={styles.summary}>{entry.summary}</span>
			</div>
			{hasDiff && (
				<details className={styles.details}>
					<summary className={styles.detailsTrigger}>View changes</summary>
					<div className={styles.diff}>
						{entry.previous_state !== null && (
							<div className={styles.diffSide}>
								<p className={styles.diffLabel}>Before</p>
								<pre className={styles.diffPre}>
									{JSON.stringify(entry.previous_state, null, 2)}
								</pre>
							</div>
						)}
						{entry.new_state !== null && (
							<div className={styles.diffSide}>
								<p className={styles.diffLabel}>After</p>
								<pre className={styles.diffPre}>{JSON.stringify(entry.new_state, null, 2)}</pre>
							</div>
						)}
					</div>
				</details>
			)}
		</li>
	);
}

// --- Component ---

export function ChangelogTimeline(props: ChangelogTimelineProps) {
	const filter =
		'scope' in props
			? { include: props.scope.include }
			: { resourceType: props.resourceType, resourceId: props.resourceId };

	const { data: entries, isLoading, error } = useChangelog(props.projectId, filter);

	if (isLoading) {
		return <p className={styles.placeholder}>Loading activity…</p>;
	}

	if (error) {
		return (
			<p className={styles.errorText}>
				{error instanceof Error ? error.message : 'Failed to load activity'}
			</p>
		);
	}

	if (!entries || entries.length === 0) {
		return <p className={styles.placeholder}>No changes recorded yet.</p>;
	}

	return (
		<ul className={styles.list}>
			{entries.map((entry) => (
				<ChangelogEntryRow key={entry.id} entry={entry} />
			))}
		</ul>
	);
}
